import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID, ViewChild, OnInit, AfterViewInit } from '@angular/core';
import { GuardsCheckEnd, NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { ErrorAlertComponent, GlobalComponentService, IndeterminateProgressBarComponent, InfoAlertComponent, InfoConfirmComponent, TmModalCloseReason, TmModalData } from '@techmore/global';
import { removeUrlQueryParam } from './util';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from './web-generator/generator-env-token';
import { merge } from 'rxjs';
import { tap, take } from 'rxjs/operators';
import { MatDialogConfig } from '@angular/material';
import { Overlay } from '@angular/cdk/overlay';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChild(InfoAlertComponent, {static: false}) infoAlert: InfoAlertComponent;
  @ViewChild(InfoConfirmComponent, {static: false}) infoConfirm: InfoConfirmComponent;
  @ViewChild(ErrorAlertComponent, {static: false}) errorAlert: ErrorAlertComponent;
  @ViewChild(IndeterminateProgressBarComponent, {static: false}) indeterminateProgressBar: IndeterminateProgressBarComponent;
  errorAlertModalConfig: MatDialogConfig<TmModalData> = {role: 'alertdialog', hasBackdrop: true }
  defaultInfoTitle = '';
  defaultInfoAlertTitle = '';
  defaultErrorAlertTitle = '';
  isBrowser = true;

  constructor(
    private globalComponentService: GlobalComponentService,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(GENERATOR_ENV_TOKEN) private generatorEnv: GeneratorEnv,
    private overlay: Overlay,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.errorAlertModalConfig = {role: 'alertdialog', hasBackdrop: true, scrollStrategy: this.overlay.scrollStrategies.noop() }
    if (this.isBrowser) {
      if (history.scrollRestoration) {
        history.scrollRestoration = 'manual';
      } else {
        window.onbeforeunload = () => {
          // console.log('onbeforeunload window.scrollY 1 : ' + window.scrollY);
          window.scrollTo(0, 0);
        };
      }
    }
  }

  ngOnInit(): void {
    this.globalComponentService.globalCmp = this;

    let isUrlPathChange = false;
    this.router.events.subscribe((evt) => {
      if (evt instanceof GuardsCheckEnd) {
        const formUrl = removeUrlQueryParam(this.router.url);
        const targetUrl = removeUrlQueryParam(evt.url);
        if (formUrl !== targetUrl) {
          isUrlPathChange = true;
        } else {
          isUrlPathChange = false;
        }
      }

      if (!(evt instanceof NavigationEnd)) {
        return;
      }

      if (this.isBrowser && isUrlPathChange) {
        window.scrollTo(0, 0);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.defaultInfoTitle = this.infoConfirm.title;
      this.defaultInfoAlertTitle = this.infoAlert.title;
      this.defaultErrorAlertTitle = this.errorAlert.title;
    }
  }

  openInfoAlert(message: string, param?: {title?: string, afterConfirm?: () => void, afterClose?: () => void}) {
    this.infoAlert.message = message;
    if (param) {
      this.infoAlert.title = param.title || this.defaultInfoAlertTitle;
      if (param.afterClose) {
        let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
        merge(
          this.infoAlert.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
          this.infoAlert.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
        ).pipe(take(1))
        .subscribe(() => {
          param.afterClose();
        });
      }
    }

    this.infoAlert.open();
  }

  openInfoConfirm(message: string, param?: {title?: string, afterConfirm?: () => void, afterClose?: () => void}) {
    this.infoConfirm.message = message;
    if (param) {
      this.infoConfirm.title = param.title || this.defaultInfoTitle;
      if (param.afterConfirm || param.afterClose) {
        let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
        merge(
          this.infoConfirm.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
          this.infoConfirm.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
        ).pipe(take(1))
        .subscribe(() => {
          if (closeReason === TmModalCloseReason.SUBMIT) {
            param.afterConfirm();
          } else {
            param.afterClose();
          }
        });
      }
    }

    this.infoConfirm.open();
  }

  openErrorAlert(message: string, param?: {title?: string, afterConfirm?: () => void, afterClose?: () => void, showCopyTip?: boolean}) {
    this.errorAlert.message = message;
    if (param) {
      this.errorAlert.title = param.title || this.defaultErrorAlertTitle;
      if (param.afterClose) {
        let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
        merge(
          this.errorAlert.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
          this.errorAlert.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
        ).pipe(take(1))
        .subscribe(() => {
          param.afterClose();
        });
      }
    }

    this.errorAlert.open();
  }

  openIndeterminateProgressBar() {
    if (this.indeterminateProgressBar) {
      this.indeterminateProgressBar.show();
    }
  }

  closeIndeterminateProgressBar() {
    if (this.indeterminateProgressBar) {
      this.indeterminateProgressBar.hide();
    }
  }
}
